/**
 * refresh middleware — 401 → 自动调 `/api/v1/auth/refresh` → 重放原请求一次。
 *
 * 设计要点：
 *  - 永远只重试一次（retryCount 是 ctx 上的单调计数器）；递归重试会形成死循环。
 *  - refresh 自身直接 fetch，不走当前链路，避免"refresh 自己 401 → 又调 refresh"。
 *  - refresh 失败：清空 token + 跳转 /login；把响应原样返回（status=401 + errorCode），
 *    让上层（features/*）自行处理。
 *  - refresh 成功：写新 token 到 sessionStorage/localStorage，再用新 access
 *    重新跑一次下游链路（next），ctx.retryCount += 1 让 telemetry 知道。
 *  - 网络层抛错时记 'NETWORK' 错误码到响应 ctx，便于 telemetry 区分。
 *  - 同 parse：用 live `globalThis.fetch` 而不是 bind 后的 snapshot，避免 MSW patch 失效。
 *
 * 配置：依赖 `baseUrl` + 后端约定的 refresh URL（与 paths.ts.apiPaths.auth.refresh 一致）。
 */

import { readRefresh, writeTokens, clearTokens, emitSessionExpired, redirectToLogin } from '../tokens';
import type { ApiResponseContext, Middleware } from './types';

export interface RefreshMiddlewareOptions {
  /** baseUrl 字符串（固定）或 getter 函数（live —— 业务侧改 client.baseUrl 后自动跟随） */
  baseUrl: string | (() => string);
  /** 后端 refresh 路径字面量；默认 '/api/v1/auth/refresh'，测试时可注入 mock URL */
  refreshPath?: string;
  /** 让测试可注入自定义 fetch；默认走 globalThis.fetch（live） */
  fetchImpl?: typeof fetch;
}

/**
 * 工厂：构造一个绑定 baseUrl 的 refresh middleware。
 */
export function createRefreshMiddleware(opts: RefreshMiddlewareOptions): Middleware {
  const refreshPath = opts.refreshPath ?? '/api/v1/auth/refresh';
  type RefreshTokens = { accessToken: string; refreshToken: string };
  type RefreshResult =
    | { kind: 'success'; tokens: RefreshTokens }
    | { kind: 'network' }
    | { kind: 'invalid' };
  let refreshPromise: Promise<RefreshResult> | null = null;
  const refreshTimeoutMs = 1800;

  // 登录、注册、刷新等认证接口的 401 是表单/凭据错误，不能把用户从登录页
  // 再重定向一次。其余 API 都属于登录后页面的受保护请求。
  const isPublicAuthPath = (path: string): boolean =>
    path.startsWith('/api/v1/auth/');

  return async function refreshMiddleware(ctx, next): Promise<ApiResponseContext> {
    const resp = await next();

    // 受保护接口即使没有 access token，也必须进入这里：可能是 access token
    // 已被清理但 refresh token 仍然有效，也可能是登录态已经完整失效。
    if (resp.status !== 401 || ctx.retryCount > 0 || isPublicAuthPath(ctx.path)) {
      return resp;
    }

    const refreshToken = readRefresh();
    if (!refreshToken) {
      clearTokens();
      emitSessionExpired();
      redirectToLogin();
      return resp;
    }

    const base = typeof opts.baseUrl === 'function' ? opts.baseUrl() : opts.baseUrl;
    const url = base.replace(/\/$/, '') + refreshPath;

    const doFetch: typeof fetch = opts.fetchImpl
      ?? ((...args: Parameters<typeof fetch>): ReturnType<typeof fetch> => fetch(...args));

    if (!refreshPromise) {
      refreshPromise = (async (): Promise<RefreshResult> => {
        let refreshResp: Response;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), refreshTimeoutMs);
        try {
          refreshResp = await doFetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
            signal: controller.signal,
          });
        } catch {
          // 网络暂时不可用不等于登录失效，保留 refresh token，待下次请求重试。
          return { kind: 'network' };
        } finally {
          clearTimeout(timeout);
        }
        // 5xx / 网关错误属于临时恢复失败，不得清理本地登录态。
        if (!refreshResp.ok && refreshResp.status >= 500) return { kind: 'network' };
        // 401/403/422 才代表刷新凭据明确失效；其它 4xx 也按临时错误处理，
        // 避免网关限流或恢复窗口把用户误判成已退出。
        if (!refreshResp.ok && ![401, 403, 422].includes(refreshResp.status)) return { kind: 'network' };
        if (!refreshResp.ok) return { kind: 'invalid' };
        try {
          const payload = await refreshResp.json() as {
            data?: { access_token?: string; refresh_token?: string };
          };
          const data = payload.data;
          if (!data?.access_token || !data.refresh_token) return { kind: 'invalid' };
          return { kind: 'success', tokens: { accessToken: data.access_token, refreshToken: data.refresh_token } };
        } catch {
          return { kind: 'network' };
        }
      })();
    }

    const refreshed = await refreshPromise;
    refreshPromise = null;
    if (refreshed.kind === 'network') {
      // 返回可重试的网络错误：不清 Token、不跳转登录页。
      return { ...resp, status: 0, ok: false, errorCode: 'NETWORK' };
    }
    if (refreshed.kind === 'invalid') {
      clearTokens();
      emitSessionExpired();
      redirectToLogin();
      return resp;
    }

    writeTokens(refreshed.tokens.accessToken, refreshed.tokens.refreshToken);
    // 重放：用新 token 替换 ctx.headers.Authorization，retryCount += 1
    ctx.headers['Authorization'] = `Bearer ${refreshed.tokens.accessToken}`;
    ctx.retryCount += 1;
    return next();
  };
}
